import React, { useState, useRef, useEffect } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useDestination } from "../../../apis/destination/DestinationProvider";
import { getUiInterface, getPageName, getWordConnector, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import Destination from "../../../types/destination";
import { FaChevronDown } from "react-icons/fa";

const DestinationCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postDestination } = useDestination();
    const navigate = useNavigate();
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const selectRef = useRef<(HTMLSelectElement | null)>(null);

    const [destination, setDestination] = useState<Destination>({
        id: 0,
        description: "",
        kind: "",
        location: {
            id: 0,
            latitude: "",
            longitude: "",
            address: {
                id: 0,
                street: "",
                number: "",
                complement: "",
                city: "",
                state: "",
                country: "",
                zipCode: ""
            }
        }
    } as Destination);

    const navItems: NavBarItem[] = [
        { label: t('destination.labels.home'), route: "/", iconName: "house-door" },
        { label: t('destination.labels.list'), route: "/Destination", iconName: "table" },
    ];

    const kinds: string[] = [ "airport", "port" ]

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setDestination((prevDestination) => {
            const keys = name.split(".");
            const updatedDestination = { ...prevDestination };
            let current: any = updatedDestination;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                current[key] = { ...current[key] };
                current = current[key];
            }

            current[keys[keys.length - 1]] = value;
            
            return updatedDestination;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!destination.description) nullRequiredFields.push("Description");
        if (!destination.kind) nullRequiredFields.push("Kind");
        if (!destination.location?.latitude) nullRequiredFields.push("Lat");
        if (!destination.location?.longitude) nullRequiredFields.push("Lng");
        if (!destination.location?.address?.street) nullRequiredFields.push("Street");
        if (!destination.location?.address?.number) nullRequiredFields.push("Number");
        if (!destination.location?.address?.complement) nullRequiredFields.push("Complement");
        if (!destination.location?.address?.city) nullRequiredFields.push("City");
        if (!destination.location?.address?.state) nullRequiredFields.push("State");
        if (!destination.location?.address?.country) nullRequiredFields.push("Country");
        // if (!destination.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

        if (nullRequiredFields.length > 0) {
            setInvalidFields(new Set(nullRequiredFields));
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        } else {
            setInvalidFields(new Set());
        }

        try {
            const response = await postDestination(destination);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createDestination", { id: response.data.id });
                navigate(`/Destination`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("createDestinationError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createDestination")}</h2>
            <Form 
                onSubmit={handleSubmit}
                style={{paddingBottom: "32px"}}
            >
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="description">
                            <Form.Label>{t('destination.fields.description')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="description"
                                value={destination.description}
                                isInvalid={invalidFields.has("Description")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('destination.fields.kind')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    as="select"
                                    name="kind"
                                    value={destination.kind}
                                    isInvalid={invalidFields.has("Kind")}
                                    onChange={handleChange}
                                    ref={(el: HTMLSelectElement | null) => {
                                        if (el) selectRef.current = el;
                                    }}
                                    style={{ 
                                        backgroundImage: 'none', 
                                        appearance: "none", 
                                        paddingRight: "30px",
                                        cursor: "pointer" 
                                    }}
                                >
                                    <option value="">{t('destination.labels.selectKind')}</option>
                                    {
                                        (kinds || []).map((kind) => (
                                            <option
                                                key={kind}
                                                value={kind}>
                                                {`${kind}`}
                                            </option>
                                        ))
                                    }
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "20px",
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => selectRef?.current?.focus()}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h5>{t('destination.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('destination.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={destination.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('destination.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={destination.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('destination.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('destination.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={destination.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('destination.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={destination.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('destination.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={destination.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('destination.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={destination.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('destination.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={destination.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('destination.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={destination.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    {/* <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={destination.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col> */}
                </Row>

                <Button variant="primary" onClick={handleSubmit}>
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default DestinationCreateView;